import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';

import apiFetch from '../../auth/apiFetch';
import { properties } from '../../properties';

const BASE = `${properties.baseURL}/admin/dan-fees`;

const parseDate = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const emptyRows = () =>
    Array.from({ length: 7 }, (_, i) => ({ dan: i + 1, examFee: null, diplomaFee: null }));

const extractError = async (res) => {
    const body = await res.json().catch(() => ({}));
    return body.error || body.message || `Erro ${res.status}`;
};

export default function DanFeeList() {
    const [vigencies, setVigencies] = useState([]);
    const [dialog, setDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [validFrom, setValidFrom] = useState('');
    const [rows, setRows] = useState(emptyRows());
    const [submitted, setSubmitted] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const toast = useRef(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const res = await apiFetch(BASE);
            if (!res.ok) throw new Error(`Erro ${res.status}`);
            const data = await res.json();
            data.sort((a, b) => b.validFrom.localeCompare(a.validFrom));
            setVigencies(data);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 4000 });
        }
    };

    const openNew = () => {
        setEditing(null);
        setValidFrom('');
        setRows(emptyRows());
        setSubmitted(false);
        setDialog(true);
    };

    const openEdit = (v) => {
        setEditing(v.validFrom);
        setValidFrom(v.validFrom);
        setRows([...v.rows].sort((a, b) => a.dan - b.dan).map(r => ({ ...r })));
        setSubmitted(false);
        setDialog(true);
    };

    const confirmDelete = (v) => {
        setDeleteTarget(v);
        setDeleteDialog(true);
    };

    const isRowValid = (r) => r.examFee != null && r.examFee >= 1 && r.diplomaFee != null && r.diplomaFee >= 1;

    const save = async () => {
        setSubmitted(true);
        if (!validFrom || rows.some(r => !isRowValid(r))) return;
        const body = { validFrom, rows };
        try {
            let saved;
            if (editing) {
                const res = await apiFetch(`${BASE}/${editing}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!res.ok) throw new Error(await extractError(res));
                saved = await res.json();
                setVigencies(prev =>
                    prev.map(v => v.validFrom === editing ? saved : v)
                        .sort((a, b) => b.validFrom.localeCompare(a.validFrom))
                );
            } else {
                const res = await apiFetch(BASE, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!res.ok) throw new Error(await extractError(res));
                saved = await res.json();
                setVigencies(prev =>
                    [...prev, saved].sort((a, b) => b.validFrom.localeCompare(a.validFrom))
                );
            }
            setDialog(false);
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Vigência salva', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 5000 });
        }
    };

    const doDelete = async () => {
        try {
            const res = await apiFetch(`${BASE}/${deleteTarget.validFrom}`, { method: 'DELETE' });
            if (res.status === 409) {
                const body = await res.json().catch(() => ({}));
                toast.current.show({
                    severity: 'warn', summary: 'Não foi possível excluir',
                    detail: body.error || body.message || 'Existem inscrições usando esta vigência',
                    life: 6000,
                });
                setDeleteDialog(false);
                return;
            }
            if (!res.ok && res.status !== 204) throw new Error(await extractError(res));
            setVigencies(prev => prev.filter(v => v.validFrom !== deleteTarget.validFrom));
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Vigência excluída', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 5000 });
        } finally {
            setDeleteDialog(false);
            setDeleteTarget(null);
        }
    };

    const updateRow = (dan, field, value) => {
        setRows(prev => prev.map(r => r.dan === dan ? { ...r, [field]: value } : r));
    };

    const dialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setDialog(false)} />
            <Button label="Salvar" icon="pi pi-check" onClick={save} />
        </>
    );

    const deleteFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteDialog(false)} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={doDelete} />
        </>
    );

    return (
        <div className="surface-card p-4 border-round shadow-2 mb-4">
            <Toast ref={toast} />
            <div className="flex justify-content-between align-items-center mb-3">
                <span className="text-2xl text-800 font-bold">Taxas por Dan</span>
                <Button label="Nova Vigência" icon="pi pi-plus" className="p-button-sm" onClick={openNew} />
            </div>

            <DataTable value={vigencies} emptyMessage="Nenhuma vigência cadastrada">
                <Column field="validFrom" header="Vigência a partir de" />
                <Column body={(row) => (
                    <>
                        <Button icon="pi pi-pencil"
                            className="p-button-rounded p-button-success mr-2 p-button-sm"
                            onClick={() => openEdit(row)} />
                        <Button icon="pi pi-trash"
                            className="p-button-rounded p-button-danger p-button-sm"
                            onClick={() => confirmDelete(row)} />
                    </>
                )} style={{ width: '9rem' }} />
            </DataTable>

            <Dialog visible={dialog} style={{ width: '700px' }}
                header={editing ? 'Editar Vigência' : 'Nova Vigência'}
                modal className="p-fluid" footer={dialogFooter}
                onHide={() => setDialog(false)}>
                <div className="formgrid grid">
                    <div className="field col-12 md:col-5">
                        <label>Data de início *</label>
                        <Calendar value={parseDate(validFrom)}
                            onChange={(e) => setValidFrom(formatDate(e.value))}
                            dateFormat="dd/mm/yy" showIcon
                            className={submitted && !validFrom ? 'p-invalid' : ''} />
                        {submitted && !validFrom && <small className="p-error">Obrigatório</small>}
                    </div>

                    <div className="col-12 mt-3">
                        <DataTable value={rows}>
                            <Column header="Dan" body={(r) => `${r.dan}º Dan`} style={{ width: '7rem' }} />
                            <Column header="Taxa de Exame (R$)" body={(r) => (
                                <InputNumber value={r.examFee}
                                    onValueChange={(e) => updateRow(r.dan, 'examFee', e.value)}
                                    mode="decimal" minFractionDigits={0} maxFractionDigits={0}
                                    useGrouping={false} min={1}
                                    className={submitted && !isRowValid(r) ? 'p-invalid' : ''} />
                            )} />
                            <Column header="Taxa de Diploma (R$)" body={(r) => (
                                <InputNumber value={r.diplomaFee}
                                    onValueChange={(e) => updateRow(r.dan, 'diplomaFee', e.value)}
                                    mode="decimal" minFractionDigits={0} maxFractionDigits={0}
                                    useGrouping={false} min={1}
                                    className={submitted && !isRowValid(r) ? 'p-invalid' : ''} />
                            )} />
                        </DataTable>
                        {submitted && rows.some(r => !isRowValid(r)) && (
                            <small className="p-error block mt-1">
                                Preencha todas as taxas com valor maior que zero
                            </small>
                        )}
                    </div>
                </div>
            </Dialog>

            <Dialog visible={deleteDialog} style={{ width: '450px' }}
                header="Confirmar Exclusão" modal footer={deleteFooter}
                onHide={() => setDeleteDialog(false)}>
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {deleteTarget && (
                        <span>Deseja excluir a vigência de <b>{deleteTarget.validFrom}</b>?</span>
                    )}
                </div>
            </Dialog>
        </div>
    );
}
